import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useT } from '~/i18n';
import PaywallModal from './PaywallModal';
import { NO_PLAN, type PlanState } from '../lib/plan';
import { planCtaCase, usePlanCta, type PlanCtaCase } from '../lib/planCta';

/**
 * Sin plan activo no sale ninguna postulación a un empleador, y hasta
 * 2026-10-10 eso no se decía en ningún lado: Felipa esperó dos días a que
 * salieran sus hojas de vida sin haber activado nunca la prueba. Ahora se dice
 * de dos maneras:
 *
 *   la cinta   en todas las pantallas del candidato, debajo de la barra, con
 *              el botón que lo resuelve;
 *   el aviso   el mismo muro de `PaywallModal`, UNA vez por sesión (al abrir la
 *              app), con un titular que dice por qué aparece.
 *
 * Ninguno sale en la pestaña Plan (ya es la oferta) ni al volver de pagar: el
 * plan tarda unos segundos en verse activo y la cinta contradiría al «listo».
 *
 * Con `free_tier` del servidor la persona postula a mano sin plan, así que no
 * tenerlo ya no es una falla: la cinta se vuelve neutra (dice qué hace el
 * plan), lila y no de advertencia, y el aviso NO se abre solo al entrar; sólo
 * desde su botón.
 */
const SEEN_KEY = 'tbit.jobapply.planNudgeSeen';
const CHECKOUT_AT_KEY = 'tbit.jobapply.checkoutReturnAt';
/** Lo que se le da al webhook de Stripe y a la caché del shell para ponerse al día. */
const CHECKOUT_GRACE_MS = 3 * 60 * 1000;

/** El caso del aviso es el caso del plan: lo decide el servidor. */
export function nudgeCase(plan: PlanState): PlanCtaCase {
  return planCtaCase(plan);
}

function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* Sin storage el aviso puede repetirse; es preferible a no avisar. */
  }
}

function justCameBackFromCheckout(search: string): boolean {
  const params = new URLSearchParams(search);
  if (params.get('checkout') === 'success' || params.get('payment') === 'success') {
    writeSession(CHECKOUT_AT_KEY, String(Date.now()));
    return true;
  }
  const at = Number(readSession(CHECKOUT_AT_KEY));
  return Number.isFinite(at) && at > 0 && Date.now() - at < CHECKOUT_GRACE_MS;
}

export default function PlanNudge({ plan, freeTier = false }: { plan: PlanState | null; freeTier?: boolean }) {
  const t = useT('plan').nudge;
  const tf = useT('workspace').dashboard.freeTier;
  const cta = usePlanCta(plan ?? NO_PLAN);
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);

  const onPlanPage = pathname.startsWith('/perfil/plan');
  const show = !!plan && !plan.plan_active && !onPlanPage && !justCameBackFromCheckout(search);

  useEffect(() => {
    if (!show || freeTier || readSession(SEEN_KEY)) return;
    writeSession(SEEN_KEY, '1');
    setOpen(true);
  }, [show, freeTier]);

  if (!plan || !show) return null;
  const copy = freeTier
    ? { ribbon: tf.ribbon, headline: tf.headline, lede: tf.lede }
    : t[nudgeCase(plan)];

  return (
    <>
      {freeTier ? (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-box px-4 py-3 mb-4 text-sm"
          style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline-hi)' }}
        >
          <span>{copy.ribbon}</span>
          <button type="button" className="btn btn-sm btn-soft btn-primary" onClick={() => setOpen(true)}>
            {cta.label}
          </button>
        </div>
      ) : (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-box px-4 py-3 mb-4 text-sm alert alert-warning"
        >
          <strong>{copy.ribbon}</strong>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => setOpen(true)}>
            {cta.label}
          </button>
        </div>
      )}
      <PaywallModal
        open={open}
        onClose={() => setOpen(false)}
        headline={copy.headline}
        lede={copy.lede}
        ctaCase={cta.case}
        surface="nudge"
        partnerDiscount={plan.partner_discount}
      />
    </>
  );
}
